import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronsUpDown, Search, X } from "lucide-react";
import { cn } from "../../../lib/utils";

export default function SearchableSelect({
  id,
  value,
  options = [],
  onChange,
  placeholder = "انتخاب کنید",
  searchPlaceholder = "جستجو...",
  disabled = false,
  error = false,
  emptyLabel = "موردی یافت نشد",
  allowCustom = false,
  customLabel = "استفاده از شناسه",
  className,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find((option) => String(option.value) === String(value));
  const displayLabel =
    selected?.label ||
    (value !== "" && value != null ? `شناسه ${value}` : "");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((option) => {
      const label = String(option.label || "").toLowerCase();
      const val = String(option.value ?? "").toLowerCase();
      return label.includes(q) || val.includes(q);
    });
  }, [options, query]);

  useEffect(() => {
    const handleClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const numericQuery =
    query.trim() !== "" && /^-?\d+$/.test(query.trim()) ? Number(query.trim()) : null;
  const customAvailable =
    allowCustom &&
    numericQuery != null &&
    !options.some((option) => String(option.value) === String(numericQuery));

  const pick = (next) => {
    onChange?.(next);
    setOpen(false);
  };

  const commitQuery = () => {
    if (customAvailable) pick(numericQuery);
  };

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((current) => !current)}
        className={cn(
          "flex w-full items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2.5 text-right text-sm transition-[border-color,box-shadow]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          error ? "border-red-400" : "border-[#d1d5db] focus-visible:border-primary",
          disabled && "cursor-not-allowed opacity-50",
        )}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className={cn("truncate", !displayLabel && "text-slate-400")}>
          {displayLabel || placeholder}
        </span>
        <span className="flex shrink-0 items-center gap-1 text-slate-400">
          {selected && !disabled ? (
            <X
              className="h-3.5 w-3.5 hover:text-slate-600"
              onClick={(event) => {
                event.stopPropagation();
                pick("");
              }}
            />
          ) : null}
          <ChevronsUpDown className="h-4 w-4" />
        </span>
      </button>

      {open ? (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
          <div className="relative border-b border-slate-100 p-2">
            <Search className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitQuery();
                }
              }}
              placeholder={
                allowCustom ? `${searchPlaceholder} یا شناسه دستی` : searchPlaceholder
              }
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pr-9 pl-3 text-sm outline-none focus:border-indigo-300 focus:bg-white"
            />
          </div>
          <ul className="max-h-64 overflow-y-auto py-1" role="listbox">
            {customAvailable ? (
              <li>
                <button
                  type="button"
                  className="flex w-full px-3 py-2 text-right text-sm font-medium text-indigo-700 hover:bg-indigo-50"
                  onClick={() => pick(numericQuery)}
                >
                  {customLabel} {numericQuery}
                </button>
              </li>
            ) : null}
            {filtered.length === 0 && !customAvailable ? (
              <li className="px-3 py-4 text-center text-sm text-slate-400">{emptyLabel}</li>
            ) : (
              filtered.map((option) => {
                const isSelected = String(option.value) === String(value);
                return (
                  <li key={String(option.value)}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={cn(
                        "flex w-full px-3 py-2 text-right text-sm hover:bg-indigo-50",
                        isSelected && "bg-indigo-50 font-medium text-indigo-800",
                      )}
                      onClick={() => pick(option.value)}
                    >
                      {option.label}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
